import React, { useEffect, useRef, useState } from 'react';
import { Stethoscope, Loader2 } from 'lucide-react';
import {
    apiService, ProcedureSuggestion, SupportedLocale, TreatmentNoteTemplate,
} from '../../../services/apiService';
import { pickLocale } from '../../../utils/i18n';

const labelClass = 'text-[11px] font-medium uppercase tracking-wider text-brand-text-muted';

interface Props {
    template: TreatmentNoteTemplate;
    sectionValues: Record<string, any>;
    locale: SupportedLocale;
}

/**
 * Live-Sidebar mit deterministischen Procedure-Vorschlaegen (Phase D).
 * Debounce 250ms — eine Reihe Klicks (z.B. mehrere Zaehne) triggert nur einen
 * Backend-Call statt einen pro Klick.
 */
const ProcedureSidebar: React.FC<Props> = ({ template, sectionValues, locale }) => {
    const [suggestions, setSuggestions] = useState<ProcedureSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef<number | null>(null);
    const tokenRef = useRef(0);

    useEffect(() => {
        if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(async () => {
            const token = ++tokenRef.current;
            setLoading(true);
            try {
                const res = await apiService.suggestProceduresForTemplate(
                    template.id,
                    sectionValues,
                );
                if (token !== tokenRef.current) return;
                setSuggestions(res.data.data?.suggestions || []);
            } catch {
                if (token === tokenRef.current) setSuggestions([]);
            } finally {
                if (token === tokenRef.current) setLoading(false);
            }
        }, 250);
        return () => {
            if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
        };
    }, [template.id, sectionValues]);

    return (
        <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm flex flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-brand-border">
                <h3 className={labelClass}>
                    <Stethoscope className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                    Procedure-Vorschläge
                </h3>
                <span className="text-[10px] text-brand-text-muted inline-flex items-center gap-1">
                    {loading
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> berechnet ...</>
                        : <>{suggestions.length} Vorschlag{suggestions.length === 1 ? '' : 'e'}</>}
                </span>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2">
                {suggestions.length === 0 && !loading && (
                    <p className="px-2 py-3 text-xs text-brand-text-muted italic">
                        Vorschläge erscheinen, sobald Sektionen ausgefüllt sind oder Template-Default-Procedures hinterlegt sind.
                    </p>
                )}
                <ul className="space-y-1.5">
                    {suggestions.map(s => {
                        const name = pickLocale(s.procedure.name, locale) || s.procedure.key;
                        const hasTeeth = s.tooth_numbers && s.tooth_numbers.length > 0;
                        return (
                            <li
                                key={s.procedure_id}
                                className="px-3 py-2 rounded-md border border-brand-border bg-brand-bg/40"
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-medium text-brand-text truncate" title={name}>
                                        {name}
                                    </span>
                                    <span className="text-xs font-mono text-brand-primary tabular-nums shrink-0">
                                        × {s.quantity}
                                    </span>
                                </div>
                                <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-brand-text-muted">
                                    <span className="truncate">
                                        {s.sources
                                            .map(src => src === '$template' ? 'Vorlage' : src)
                                            .join(' · ')}
                                    </span>
                                    {hasTeeth && (
                                        <span className="font-mono text-brand-text-muted shrink-0" title={s.tooth_numbers!.join(', ')}>
                                            {s.tooth_numbers!.length > 4
                                                ? `${s.tooth_numbers!.slice(0, 3).join(',')} +${s.tooth_numbers!.length - 3}`
                                                : s.tooth_numbers!.join(', ')}
                                        </span>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {suggestions.length > 0 && (
                <div className="shrink-0 border-t border-brand-border px-5 py-2 text-[10px] text-brand-text-muted">
                    Beim Speichern können diese Procedures in eine Rechnung übernommen werden.
                </div>
            )}
        </div>
    );
};

export default ProcedureSidebar;
